import React from 'react';
import { Check, CheckCircle2, Eye, FileText, X, XCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';

const WORKFLOW_STEPS = [
  ['awaiting_trainer', 'Student upload', 'Marked script submitted'],
  ['awaiting_department', 'Trainer verification', 'Assigned trainer verifies the marked script'],
  ['awaiting_final', 'CoD / Head of Section', 'Department leadership checks the evidence'],
  ['approved', 'Final approval', 'Dean, HOD or POE Manager approves'],
  ['verifier', 'Verifier access', 'Approved evidence is available as read-only']
];

const stageIndex = stage => {
  return ({ awaiting_trainer: 1, awaiting_department: 2, awaiting_final: 3, approved: 4 }[stage] ?? 0);
};

const formatVerificationDate = value => value
  ? new Date(value).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })
  : '';

const POEApprovalWorkflow = ({ isOpen, onClose, submission, onSubmit, onExternalVerify, onViewFile, userRole }) => {
  const [comments, setComments] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (isOpen) {
      setComments('');
      setError('');
    }
  }, [isOpen, submission?.evidence_id]);

  if (!isOpen || !submission) return null;

  const currentIndex = stageIndex(submission.workflow_stage);
  const isExternalVerifierView = userRole === 'verifier';
  // Never trust the row capability flag on its own. Only the dedicated
  // external-verifier view may render or submit the external verification
  // action.
  const canExternallyVerify = isExternalVerifierView && Boolean(submission.external_can_verify ?? submission.can_verify);
  const act = async action => {
    setSaving(true);
    setError('');
    try {
      await onSubmit(action, comments);
    } catch (reviewError) {
      setError(reviewError?.message || 'The POE review could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1050] grid place-items-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-xl border bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-950">{submission.unit_code} · CAM {submission.assessment_number}</h2>
            <p className="mt-0.5 text-sm text-slate-500">{submission.student_name} · {submission.student_number}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-5">
          <button onClick={onViewFile} disabled={!submission.file_path} className="flex w-full items-center gap-3 rounded-xl border bg-slate-50 p-4 text-left hover:border-primary/40 disabled:opacity-50">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><FileText className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1"><b className="block truncate text-sm">{submission.file_name || 'No script uploaded'}</b><small className="text-slate-500">Open scanned marked script</small></span>
            <Eye className="h-4 w-4 text-slate-400" />
          </button>

          {!isExternalVerifierView && (submission.workflow_stage === 'rejected' ? (
            <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <div className="flex items-center gap-2 font-semibold"><XCircle className="h-5 w-5" />Returned to the student</div>
              <p className="mt-1">{submission.review_comments || 'Upload a corrected scan for review.'}</p>
            </div>
          ) : (
            <ol className="mt-5 space-y-2">
              {WORKFLOW_STEPS.map(([id, title, description], index) => {
                const completed = index < currentIndex;
                const current = index === currentIndex;
                return (
                  <li key={id} className={`flex gap-3 rounded-lg border p-3 ${current ? 'border-primary/30 bg-primary/5' : 'border-slate-200'}`}>
                    <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${completed ? 'bg-emerald-100 text-emerald-700' : current ? 'bg-primary text-primary-foreground' : 'bg-slate-100 text-slate-400'}`}>
                      {completed ? <Check className="h-3.5 w-3.5" /> : index + 1}
                    </span>
                    <span><b className="block text-sm text-slate-900">{title}</b><small className="text-slate-500">{description}</small></span>
                  </li>
                );
              })}
            </ol>
          ))}

          {submission.student_note && <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600"><b>Student note:</b> {submission.student_note}</p>}
          {submission.review_comments && submission.workflow_stage !== 'rejected' && <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600"><b>Latest review:</b> {submission.review_comments}</p>}

          {(submission.trainer_verified_at || submission.external_verified_at) && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
              {submission.trainer_verified_at && <p><b className="text-slate-800">Internally Verified By:</b> {submission.trainer_name || 'Trainer'} · {formatVerificationDate(submission.trainer_verified_at)}</p>}
              {submission.external_verified_at && <p className="mt-1"><b className="text-slate-800">Externally Verified By:</b> {submission.external_verifier_name || 'External verifier'} · {formatVerificationDate(submission.external_verified_at)}</p>}
              {submission.external_verification_comments && <p className="mt-1 text-slate-500">External note: {submission.external_verification_comments}</p>}
            </div>
          )}

          {canExternallyVerify && onExternalVerify && (
            <div className="mt-5 border-t border-slate-200 pt-4">
              <label className="text-sm font-semibold text-slate-700">External verification note (optional)
                <textarea value={comments} onChange={event => setComments(event.target.value)} rows="3" className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" placeholder="Record the scope or reference for this external verification" />
              </label>
              {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
              <div className="mt-4 flex justify-end">
                <Button onClick={async () => {
                  setSaving(true);
                  setError('');
                  try { await onExternalVerify(comments); }
                  catch (verifyError) { setError(verifyError?.message || 'External verification could not be saved.'); }
                  finally { setSaving(false); }
                }} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle2 className="mr-1.5 h-4 w-4" />{saving ? 'Saving…' : 'Verify externally'}
                </Button>
              </div>
            </div>
          )}

          {submission.can_review && (
            <div className="mt-5 border-t pt-4">
              <label className="text-sm font-semibold text-slate-700">Review comment
                <textarea value={comments} onChange={event => setComments(event.target.value)} rows="3" className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" placeholder="Add approval notes or explain why the script is being returned" />
              </label>
              {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => act('reject')} disabled={saving} className="text-red-700"><XCircle className="mr-1.5 h-4 w-4" />Return</Button>
                <Button onClick={() => act('approve')} disabled={saving}><CheckCircle2 className="mr-1.5 h-4 w-4" />{saving ? 'Saving…' : 'Approve'}</Button>
              </div>
            </div>
          )}

          {submission.read_only && !canExternallyVerify && <p className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">Verifier access is read-only. This evidence has completed institutional approval.</p>}
          {!submission.can_review && !submission.read_only && userRole !== 'student' && <p className="mt-4 text-xs text-slate-500">This item is waiting at another approval stage.</p>}
        </div>
      </div>
    </div>
  );
};

export default POEApprovalWorkflow;
